import generateErrorMessage from '../../helper-functions/generateErrorMessage';
import isNotEmptyString from '../../helper-functions/isNotEmptyString';

type Credentials = Record<string, any>;

const isPlainObject = (value: unknown): value is Credentials =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * The credential fields as central-api expects them. An account picked as
 * "manual" (`FIELD: 'manual_account'` plus the typed `FIELD_manual`) is sent as
 * `FIELD: <typed value>`, and the `_manual` helper keys are dropped.
 */
export function formatMcpCredentials(credentials: unknown): Credentials {
  const formatted: Credentials = {};
  if (!isPlainObject(credentials)) {
    return formatted;
  }

  for (const key of Object.keys(credentials)) {
    const value = credentials[key];
    const hasManualValue = Object.prototype.hasOwnProperty.call(
      credentials,
      `${key}_manual`
    );

    if (value === 'manual_account' && hasManualValue) {
      formatted[key] = credentials[`${key}_manual`];
    } else if (!key.endsWith('_manual')) {
      formatted[key] = value;
    }
  }

  return formatted;
}

const comparable = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value).trim();

/**
 * True when the credential form holds a value that differs from what the
 * connection was opened with. Blank and missing count as the same thing, so
 * focusing a field and clearing it again is not an edit.
 */
export function hasMcpCredentialChanges(
  current: unknown,
  baseline: unknown
): boolean {
  const now = formatMcpCredentials(current);
  const before = formatMcpCredentials(baseline);
  const keys = new Set([...Object.keys(now), ...Object.keys(before)]);

  return Array.from(keys).some(
    (key) => comparable(now[key]) !== comparable(before[key])
  );
}

export type UpdateMcpBody = {
  credentials?: Credentials;
  is_active: boolean;
  oauth_code?: string;
};

/**
 * Request body for `PATCH …/credentials/{id}`. The `credentials` key is left
 * out when there is nothing to send: central-api then keeps the stored
 * credentials and only applies `is_active`. Sending an empty object instead
 * asks it to replace them with nothing, which an OAuth platform (whose list
 * entry carries no credential values) cannot pass.
 */
export function buildUpdateMcpBody(
  credentials: unknown,
  isActive: boolean,
  oauthCode?: string
): UpdateMcpBody {
  const body: UpdateMcpBody = { is_active: isActive };

  const formatted = formatMcpCredentials(credentials);
  if (Object.keys(formatted).length > 0) {
    body.credentials = formatted;
  }
  if (oauthCode !== undefined) {
    body.oauth_code = oauthCode;
  }

  return body;
}

/**
 * The reason central-api gave for refusing a credential save, or `undefined`
 * when the response carried none (no body, a network error, a non-JSON
 * gateway page). The caller supplies its own translated fallback.
 */
export function getMcpErrorMessage(response: unknown): string | undefined {
  if (!isPlainObject(response)) {
    return undefined;
  }

  const hasMessage =
    typeof response.message === 'string' && isNotEmptyString(response.message);
  const hasErrors =
    !!response.errors &&
    typeof response.errors === 'object' &&
    Object.keys(response.errors).length > 0;

  if (!hasMessage && !hasErrors) {
    return undefined;
  }

  const message = generateErrorMessage(response);
  return isNotEmptyString(message) ? message : undefined;
}
